/**
 * A ficha aberta: o que o card esconde.
 *
 * <p>A estrutura segue a ficha do jogo — nível e XP no alto, as três condições primárias
 * em barras (Vigor, Concentração e Carga), os seis atributos em duas colunas com a sigla
 * colorida, as medidas de combate em grade, as informações do corpo e as perícias em
 * blocos. A pele é a do GG Pilot, não o pergaminho
 * do jogo: duas peles no mesmo produto fariam a tela parecer um site de outra pessoa.
 *
 * <p><b>Quem escreve o quê.</b> Três regras diferentes convivem nesta tela, e confundi-las
 * é o defeito mais fácil de cometer aqui:
 *
 * <ul>
 *   <li><b>Os atributos</b> são gastos pelo dono na CRIAÇÃO, em {@link ./CriarPersonagem}
 *       — não aqui. Os botões + e − desta tela são do mestre, e servem para corrigir uma
 *       distribuição errada, não para o jogador refazer a build no meio da sessão.</li>
 *   <li><b>O XP das perícias</b> é dado pelo mestre durante o jogo, e nunca na criação:
 *       perícia se ganha usando.</li>
 *   <li><b>O resto</b> (HP, combate, itens, condição) é do mestre.</li>
 * </ul>
 *
 * <p>O que não se pode editar vem em somente-leitura, e não some: o jogador precisa ler a
 * própria ficha durante a sessão, e esconder o que não se edita esconde o que se veio
 * consultar.
 */
import { useEffect, useState } from 'react';

import { ErroApi } from '../../api/cliente';
import { ATRIBUTOS } from '../gamedesign/atributos';
import * as fichasApi from './fichasApi';
import {
  CAMPOS_DE_COMBATE,
  GRUPOS_DE_PERICIA,
  PONTOS_DE_ATRIBUTO,
  custoDoProximoPonto,
  fracao,
  interpretarFicha,
  nivelDaPericia,
  nivelDoPersonagem,
  pontosRestantes,
  xpDoProximoNivel,
  xpParaSubirPericia,
} from './fichaDeMistikDream';
import type { Ficha, Medida } from './fichaDeMistikDream';
import './fichas.css';

export function FichaCompleta({
  ficha,
  souMestre,
  aoFechar,
  aoGravar,
  aoExcluir,
}: {
  ficha: fichasApi.Ficha;
  souMestre: boolean;
  aoFechar: () => void;
  aoGravar: (atualizada: fichasApi.Ficha) => void;
  aoExcluir: () => void;
}) {
  const [f, setF] = useState<Ficha>(() => interpretarFicha(ficha.conteudo));
  const [nome, setNome] = useState(ficha.nome);
  const [sujo, setSujo] = useState(false);
  const [gravando, setGravando] = useState(false);
  const [erro, setErro] = useState('');

  // Esc fecha: o pop-up cobre a tela, e sair dele tem de ser mais fácil que achar o ×.
  useEffect(() => {
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') aoFechar();
    };
    document.addEventListener('keydown', aoTeclar);
    return () => document.removeEventListener('keydown', aoTeclar);
  }, [aoFechar]);

  const mudar = (muda: (antes: Ficha) => Ficha) => {
    setSujo(true);
    setF(muda);
  };

  async function gravar() {
    setGravando(true);
    setErro('');
    try {
      const salva = await fichasApi.salvar(ficha.id, {
        nome,
        conteudo: f,
        versaoBase: ficha.versao,
      });
      setSujo(false);
      aoGravar(salva);
    } catch (e) {
      setErro(
        e instanceof ErroApi
          ? e.message
          : `Não consegui gravar: ${e instanceof Error ? e.message : 'erro desconhecido'}.`
      );
    } finally {
      setGravando(false);
    }
  }

  /** Só-leitura para o grosso da ficha. */
  const so = !souMestre;
  const nivel = nivelDoPersonagem(f.xp);
  const restantes = pontosRestantes(f.atributos);
  /*
   * Os pontos de atributo são gastos na CRIAÇÃO, pelo dono, no formulário da mesa — e
   * não aqui. Depois de criada a ficha, quem mexe em qualquer coisa dela é o mestre,
   * que é a regra combinada. Por isso os botões + e − são dele, e servem para corrigir
   * uma distribuição errada, não para o jogador refazer a build no meio da sessão.
   */
  const podeGastarPontos = souMestre;

  return (
    <div className="fd-visor" onClick={aoFechar}>
      <div className="fd-caixa" onClick={(e) => e.stopPropagation()}>
        <header className="fd-topo" style={{ borderColor: ficha.corDoDono }}>
          <Retrato ficha={ficha} />
          <div className="fd-titulo">
            <input
              className="fd-nome"
              value={nome}
              readOnly={so}
              aria-label="Nome do personagem"
              onChange={(e) => {
                setNome(e.target.value);
                setSujo(true);
              }}
            />
            <span className="fd-dono" style={{ color: ficha.corDoDono }}>
              {ficha.nomeDoDono}
            </span>
          </div>
          <button type="button" className="fd-fechar" onClick={aoFechar} title="Fechar (Esc)">
            ✕
          </button>
        </header>

        <div className="fd-corpo">
          {so && (
            <p className="fd-aviso">
              Você está lendo a ficha. Quem escreve depois de criada é o <b>mestre</b> — é a
              regra da mesa, não uma limitação da tela. Os pontos de atributo você gastou
              na criação.
            </p>
          )}

          {/* --------------------------------------------- nível, XP e vitais */}
          <section className="fd-secao">
            <div className="fd-nivel">
              <span className="fd-rotulo">Nível</span>
              <b className="fd-nivel-numero">{nivel}</b>
              <span className="fd-nivel-xp">
                <NumeroEditavel
                  valor={f.xp}
                  somenteLeitura={so}
                  rotulo="XP acumulado"
                  aoMudar={(v) => mudar((a) => ({ ...a, xp: v }))}
                />
                <span className="fd-barra-sep">/</span>
                <span className="fd-alvo">{xpDoProximoNivel(nivel)}</span>
                <span className="fd-xp-rotulo">XP para o nível {nivel + 1}</span>
              </span>
            </div>
            <div className="fd-trilho fd-trilho-xp">
              <i
                style={{
                  width: `${fracao({ atual: f.xp, max: xpDoProximoNivel(nivel) }) * 100}%`,
                  background: 'var(--gold)',
                }}
              />
            </div>

            {/*
              As três condições primárias do personagem, como o GDD as chama — Vigor,
              a energia de agir (que a mesa chama de Concentração) e a Carga. A barra de
              Carga enche conforme o peso: cheia é "no limite", e é o sinal que a mesa
              olha antes de deixar o personagem pegar mais loot.
            */}
            <Barra
              rotulo="Vigor (HP)"
              medida={f.hp}
              cor="#b0544d"
              somenteLeitura={so}
              aoMudar={(m) => mudar((a) => ({ ...a, hp: m }))}
            />
            <Barra
              rotulo="Concentração (SP)"
              medida={f.sp}
              cor="#5b8fb9"
              somenteLeitura={so}
              aoMudar={(m) => mudar((a) => ({ ...a, sp: m }))}
            />
            <Barra
              rotulo="Carga"
              medida={f.peso}
              cor="#c2954f"
              somenteLeitura={so}
              aoMudar={(m) => mudar((a) => ({ ...a, peso: m }))}
            />
          </section>

          {/* ---------------------------------------------------- atributos */}
          <h3 className="fd-divisor">Atributos</h3>
          <p className="fd-pontos">
            <span className="fd-rotulo">Pontos restantes</span>
            <b className={restantes < 0 ? 'fd-restantes negativo' : 'fd-restantes'}>
              {restantes}
            </b>
            <span className="fd-de">de {PONTOS_DE_ATRIBUTO}</span>
            <span className="fd-explica">
              Subir custa o número aonde se chega: de 0 para 1 custa 1, de 3 para 4 custa 4.
            </span>
          </p>

          <div className="fd-atributos">
            {ATRIBUTOS.map((a) => {
              const valor = f.atributos[a.sigla];
              const custo = custoDoProximoPonto(valor);
              const trocar = (novo: number) =>
                mudar((f0) => ({ ...f0, atributos: { ...f0.atributos, [a.sigla]: novo } }));

              return (
                <div className="fd-atributo" key={a.sigla}>
                  <b style={{ color: a.cor }}>{a.sigla}</b>
                  <span className="fd-atributo-nome">{a.nome}</span>

                  <div className="fd-atributo-valores">
                    {podeGastarPontos && (
                      <button
                        type="button"
                        className="fd-passo"
                        onClick={() => trocar(valor - 1)}
                        disabled={valor <= 0}
                        title={`Devolver ${valor} ponto(s)`}
                        aria-label={`Baixar ${a.nome}`}
                      >
                        −
                      </button>
                    )}
                    <span className="fd-atributo-base">{valor}</span>
                    {podeGastarPontos && (
                      <button
                        type="button"
                        className="fd-passo"
                        onClick={() => trocar(valor + 1)}
                        disabled={custo > restantes}
                        title={`Custa ${custo} ponto(s)`}
                        aria-label={`Subir ${a.nome}`}
                      >
                        +
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* ------------------------------------------------------ combate */}
          <h3 className="fd-divisor">Combate</h3>
          <div className="fd-combate">
            {CAMPOS_DE_COMBATE.map((c) => (
              <label className="fd-medida" key={c.id}>
                <span>{c.rotulo}</span>
                <input
                  value={f.combate[c.id]}
                  placeholder={c.dica}
                  readOnly={so}
                  onChange={(e) =>
                    mudar((a) => ({ ...a, combate: { ...a.combate, [c.id]: e.target.value } }))
                  }
                />
              </label>
            ))}
          </div>

          {/* -------------------------------------------------- informações */}
          <h3 className="fd-divisor">Informações</h3>
          {/*
            O peso não mora mais aqui: a Carga subiu para as barras do topo, junto de
            Vigor e Concentração — é condição primária (pedido do Theo, 19/08/2026). O
            mesmo número em dois editores na mesma tela é a régua duplicada de sempre.
          */}
          <div className="fd-informacoes">
            <label className="fd-linha">
              <span>Condição</span>
              <input
                value={f.condicao}
                placeholder="Normal"
                readOnly={so}
                onChange={(e) => mudar((a) => ({ ...a, condicao: e.target.value }))}
              />
            </label>
          </div>

          {/* ------------------------------------------------------- itens */}
          <h3 className="fd-divisor">Itens</h3>
          <ol className="fd-itens">
            {f.itens.map((item, i) => (
              // O índice como chave é seguro aqui, e só aqui: a lista não reordena nem
              // remove do meio — ela só cresce no fim.
              <li key={i}>
                <input
                  value={item}
                  placeholder="—"
                  readOnly={so}
                  aria-label={`Item ${i + 1}`}
                  onChange={(e) =>
                    mudar((a) => ({
                      ...a,
                      itens: a.itens.map((v, j) => (j === i ? e.target.value : v)),
                    }))
                  }
                />
              </li>
            ))}
          </ol>
          {souMestre && (
            <button
              type="button"
              className="fd-mais-linha"
              onClick={() => mudar((a) => ({ ...a, itens: [...a.itens, ''] }))}
            >
              + linha
            </button>
          )}

          {/* ----------------------------------------------------- perícias */}
          <h3 className="fd-divisor">Perícias</h3>
          <p className="fd-explica solta">
            Toda perícia começa no nível 0, e o XP vem de usá-la: 1 para o nível 1, 3 para o
            2, 6 para o 3. Quem dá o ponto é o <b>mestre</b>, durante o jogo — perícia não
            se compra na criação.
          </p>

          {GRUPOS_DE_PERICIA.map((g) => (
            <div className="fd-grupo" key={g.titulo}>
              <h4>{g.titulo}</h4>
              <div className="fd-pericias">
                {g.pericias.map((p) => {
                  const xp = f.pericias[p.id] ?? 0;
                  const somar = (passo: number) =>
                    mudar((a) => ({
                      ...a,
                      pericias: { ...a.pericias, [p.id]: Math.max(0, xp + passo) },
                    }));

                  return (
                    <div className="fd-pericia" key={p.id}>
                      {/*
                        A cor da perícia vem do catálogo, e NÃO por `corDe`: aquela função
                        acinzenta o que fica para depois da Season 1 do jogo — e a mesa de
                        Mistik Dream usa magia justamente por ser experimental.
                      */}
                      <span className="fd-pericia-nome" style={{ color: p.cor }}>
                        {p.rotulo}
                      </span>
                      <span className="fd-lv">LV {nivelDaPericia(xp)}</span>
                      <span className="fd-xp">
                        {xp} / {xpParaSubirPericia(xp)} XP
                      </span>
                      {souMestre && (
                        <span className="fd-bts-xp">
                          <button
                            type="button"
                            className="fd-passo"
                            onClick={() => somar(-1)}
                            disabled={xp <= 0}
                            aria-label={`Tirar 1 XP de ${p.rotulo}`}
                          >
                            −
                          </button>
                          <button
                            type="button"
                            className="fd-passo"
                            onClick={() => somar(1)}
                            aria-label={`Dar 1 XP a ${p.rotulo}`}
                          >
                            +
                          </button>
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {souMestre && (
          <footer className="fd-rodape">
            <button
              type="button"
              className="fd-gravar"
              onClick={() => void gravar()}
              disabled={!sujo || gravando}
            >
              {gravando ? 'Gravando…' : 'Salvar ficha'}
            </button>
            <span className="fd-pendencia">
              {erro || (sujo ? 'Há mudanças não gravadas.' : 'Sem mudanças a gravar.')}
            </span>
            {souMestre && (
              // No rodapé, e não solto na página: fora do pop-up ele ficaria ATRÁS do
              // visor, que cobre a tela inteira — visível e inalcançável.
              <button type="button" className="fd-apagar" onClick={aoExcluir}>
                Apagar ficha
              </button>
            )}
          </footer>
        )}
      </div>
    </div>
  );
}

/** A foto do personagem, ou a do dono, ou as iniciais sobre a cor dele. */
function Retrato({ ficha }: { ficha: fichasApi.Ficha }) {
  const foto = ficha.imagem || ficha.avatarDoDono;
  if (foto) return <img className="fd-retrato" src={foto} alt="" />;
  return (
    <span className="fd-retrato iniciais" style={{ background: ficha.corDoDono }}>
      {ficha.nome.trim().charAt(0).toUpperCase() || '?'}
    </span>
  );
}

/** Uma barra com o número ao lado — as três condições primárias: Vigor, Concentração e Carga. */
function Barra({
  rotulo,
  medida,
  cor,
  somenteLeitura,
  aoMudar,
}: {
  rotulo: string;
  medida: Medida;
  cor: string;
  somenteLeitura: boolean;
  aoMudar: (m: Medida) => void;
}) {
  return (
    <div className="fd-barra">
      <span className="fd-rotulo">{rotulo}</span>
      <div className="fd-trilho">
        <i style={{ width: `${fracao(medida) * 100}%`, background: cor }} />
      </div>
      <span className="fd-par">
        <NumeroEditavel
          valor={medida.atual}
          somenteLeitura={somenteLeitura}
          rotulo={`${rotulo} atual`}
          aoMudar={(v) => aoMudar({ ...medida, atual: v })}
        />
        <span className="fd-barra-sep">/</span>
        <NumeroEditavel
          valor={medida.max}
          somenteLeitura={somenteLeitura}
          rotulo={`${rotulo} máximo`}
          aoMudar={(v) => aoMudar({ ...medida, max: v })}
        />
      </span>
    </div>
  );
}

/**
 * Um número que se digita.
 *
 * <p>`type="text"` com `inputMode="numeric"`, e não `type="number"`: o campo numérico
 * some com o valor quando o texto fica momentaneamente inválido (apagar o único dígito
 * antes de digitar outro), e numa mesa isso vira "o HP do Balka sumiu". Aqui o campo
 * guarda o que foi digitado e converte na leitura — vazio vale zero.
 */
function NumeroEditavel({
  valor,
  somenteLeitura,
  rotulo,
  aoMudar,
}: {
  valor: number;
  somenteLeitura: boolean;
  rotulo: string;
  aoMudar: (v: number) => void;
}) {
  return (
    <input
      className="fd-numero"
      type="text"
      inputMode="numeric"
      value={String(valor)}
      readOnly={somenteLeitura}
      aria-label={rotulo}
      onChange={(e) => {
        const limpo = e.target.value.replace(/[^\d-]/g, '');
        aoMudar(limpo === '' || limpo === '-' ? 0 : Number(limpo));
      }}
    />
  );
}
